import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

const trainers = [
  {
    name: "Aarav Sharma",
    specialty: "Strength & Conditioning",
    experience: "7+ years experience • CSCS Certified • Former Competitive Athlete",
    focus: "Specializes in hybrid coaching across gyms, parks, and home workouts. Expert in powerlifting, Olympic lifting, and athletic performance enhancement.",
    achievements: "Helped 200+ clients achieve strength goals",
    image: "/trainer1.png",
  },
  {
    name: "Meera Patel",
    specialty: "Weight Loss & Lifestyle Transformation",
    experience: "5+ years experience • Certified Nutrition Coach • Psychology Background",
    focus: "Expert in sustainable fat loss through habit-based training and lifestyle modifications. Believes in making fitness enjoyable and maintainable.",
    achievements: "Average client weight loss: 15-25kg in 6 months",
    image: "/trainer2.png",
  },
  {
    name: "Rohan Verma",
    specialty: "Mobility & Functional Fitness",
    experience: "6+ years experience • Functional Movement Specialist • Corrective Exercise Expert",
    focus: "Specializes in pain-free movement, injury prevention, and posture correction. Perfect for desk workers and those recovering from injuries.",
    achievements: "95% client injury recovery rate",
    image: "/trainer3.png",
  },
];

export default function TrainersPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Trainers",
        item: "https://fiteasy.com/trainers",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Trainers"
        description="Discover certified FitEasy trainers for personal training, group classes, and online coaching tailored to your goals. All trainers are vetted for expertise, safety, and client results."
        path="/trainers"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <SectionHeader
            id="trainers-heading"
            label="Trainers"
            title="Train with certified experts who are invested in your success"
            description="FitEasy connects you with a network of verified, certified trainers who are passionate about helping you achieve your goals. Every trainer on our platform undergoes a rigorous vetting process that includes credential verification, background checks, client testimonials review, and ongoing performance monitoring. Whether you're looking for one-on-one personal training, group park classes, or online coaching, we match you with professionals who understand your unique needs and are committed to your long-term success."
            className="mb-12"
          />

          <section aria-labelledby="trainers-heading" className="grid gap-8 md:grid-cols-3">
            {trainers.map((trainer) => (
              <article
                key={trainer.name}
                className="flex flex-col items-center rounded-3xl border border-blue-100 bg-white/90 p-6 text-center shadow-[0_18px_60px_rgba(59,130,246,0.08)] transition hover:shadow-[0_18px_60px_rgba(59,130,246,0.15)] focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2"
              >
                <div className="relative mb-4 h-28 w-28 overflow-hidden rounded-full border border-blue-100 bg-blue-50">
                  <img
                    src={trainer.image}
                    alt={`${trainer.name} - ${trainer.specialty}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h2 className="font-display text-lg text-gray-900">
                  {trainer.name}
                </h2>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                  {trainer.specialty}
                </p>
                <p className="mt-3 text-sm text-gray-700 font-medium">{trainer.experience}</p>
                <p className="mt-2 text-xs text-gray-600 leading-relaxed">{trainer.focus}</p>
                {trainer.achievements && (
                  <p className="mt-3 text-xs text-blue-600 font-semibold border-t border-blue-100 pt-3">
                    {trainer.achievements}
                  </p>
                )}
                <CTAButton
                  href="/trainers"
                  variant="outline"
                  className="mt-4 text-xs px-4 py-2"
                  ariaLabel={`View ${trainer.name}'s profile`}
                >
                  View Profile
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </CTAButton>
              </article>
            ))}
          </section>

          <section aria-labelledby="how-it-works-heading" className="mt-12 rounded-3xl border border-blue-100 bg-white/90 p-6 sm:p-8">
            <SectionHeader
              id="how-it-works-heading"
              label="How It Works"
              title="How trainer discovery works"
              className="mb-6"
            />
            <ol className="grid gap-4 text-sm text-gray-700 sm:grid-cols-3" role="list">
              <li className="space-y-2" role="listitem">
                <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                  Step 1: Complete your fitness profile
                </span>
                <p className="leading-relaxed">
                  In the app, share your fitness goals, current activity level, schedule preferences, preferred training locations (gym, home, park), any injuries or limitations, and what motivates you. Our algorithm uses this information to match you with trainers who specialize in your needs.
                </p>
              </li>
              <li className="space-y-2" role="listitem">
                <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                  Step 2: Browse and compare trainers
                </span>
                <p className="leading-relaxed">
                  Review trainer profiles, read detailed bios, check certifications, browse client reviews and success stories, compare pricing and availability, and watch trainer introduction videos. Filter by specialty, location, price range, and ratings to find your perfect match.
                </p>
              </li>
              <li className="space-y-2" role="listitem">
                <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                  Step 3: Book your first session
                </span>
                <p className="leading-relaxed">
                  Book your first session directly through the app, choose your preferred time slot, complete secure payment, and receive session details. After your session, track your progress, communicate with your trainer, and adjust your plan as needed—all in one place.
                </p>
              </li>
            </ol>
            <div className="mt-8 flex justify-center">
              <CTAButton
                href="/programs"
                variant="primary"
                ariaLabel="Download the FitEasy app to find trainers"
              >
                Download App to Get Started
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </CTAButton>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

